import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Toast from 'react-hot-toast';
import { CatalogForm } from '../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../components/CatalogForm/validators';
import { UnidadTransporteService } from '../../../services/UnidadTransporteService';
import { AuthContext } from '../../../context/authContext';
import ACCESS from '../../../ACCESS';

const UnidadTransporteForm = () => {
  const routeParams = useParams() || {};
  const id = routeParams.id || null;
  const auth = useContext(AuthContext);
  const hasAccess = useCallback(
    (permission) => (auth && auth.hasAccess ? auth.hasAccess(permission) : true),
    [auth]
  );

  // Options states
  const [sucursalesOptions, setSucursalesOptions] = useState([]);
  const [transportistasOptions, setTransportistasOptions] = useState([]);
  const [coloniasOptions, setColoniasOptions] = useState([]);
  const [cpOptions, setCpOptions] = useState([]);

  // Driver active / modal states
  const [activo, setActivo] = useState(true);
  const [showPassModal, setShowPassModal] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [temporalPass, setTemporalPass] = useState(null);

  const [showActiveModal, setShowActiveModal] = useState(false);
  const [savingActive, setSavingActive] = useState(false);

  // Load initial form options (sucursales, transportistas)
  useEffect(() => {
    let isMounted = true;
    const fetchFormOptions = async () => {
      const data = await UnidadTransporteService.obtenerOpcionesForm(id ? { id_unidad_transporte: id } : {});
      if (isMounted && data) {
        if (data.sucursales) {
          setSucursalesOptions(data.sucursales.map((s) => ({ value: s.Value, label: s.Label })));
        }
        if (data.transportistas) {
          setTransportistasOptions(data.transportistas.map((t) => ({ value: t.Value, label: t.Label })));
        }
        if (data.colonias && Array.isArray(data.colonias)) {
          setColoniasOptions(data.colonias.map((c) => ({ value: c.Value, label: c.Label })));
        }
      }
    };
    fetchFormOptions();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Handle Postal Code lookup
  const handleSearchCp = useCallback(async (query) => {
    if (!query || query.trim().length < 5) return [];
    const res = await UnidadTransporteService.buscarCodigoPostal(query.trim());
    if (res && Array.isArray(res)) {
      return res.map((item) => ({
        value: item.Value || item.codigo_postal,
        label: `${item.Label || item.codigo_postal} (${item.ciudad}, ${item.estado})`,
      }));
    }
    return [];
  }, []);

  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    if (!val || String(val).trim().length < 5) return;
    const cleanCp = String(val).trim().substring(0, 5);
    const res = await UnidadTransporteService.buscarCodigoPostal(cleanCp);
    if (res && res.length > 0) {
      const cpData = res[0];
      setFieldValue('estado', cpData.estado || '');
      setFieldValue('ciudad', cpData.ciudad || '');
      if (cpData.colonias && Array.isArray(cpData.colonias)) {
        const mappedCols = cpData.colonias.map((c) => ({ value: c.Value, label: c.Label }));
        setColoniasOptions(mappedCols);
        if (mappedCols.length === 1) {
          setFieldValue('chofer_id_colonia', mappedCols[0].value);
        } else {
          setFieldValue('chofer_id_colonia', '');
        }
      }
    }
  }, []);

  // Pre-load data in edit mode
  const loadData = useCallback(async (recordId) => {
    const data = await UnidadTransporteService.obtenerPorId(recordId);
    if (data) {
      setActivo(data.activo !== false && data.activo !== 0);

      // Pre-populate CP and colonias
      if (data.codigo_postal) {
        setCpOptions([{ value: data.codigo_postal, label: data.codigo_postal }]);
        // Fetch all colonias for this CP
        const cpRes = await UnidadTransporteService.buscarCodigoPostal(data.codigo_postal);
        if (cpRes && cpRes[0] && cpRes[0].colonias) {
          setColoniasOptions(cpRes[0].colonias.map((c) => ({ value: c.Value, label: c.Label })));
        } else if (data.id_colonia && data.colonia) {
          setColoniasOptions([{ value: data.id_colonia, label: data.colonia }]);
        }
      }
    }
    return data;
  }, []);

  // Submit handler
  const handleSubmit = useCallback(async (values) => {
    const payload = {
      ...values,
      id_unidad_transporte: id,
    };
    return await UnidadTransporteService.guardar(payload);
  }, [id]);

  // Generate temporary password handler
  const handleGeneratePassword = async () => {
    if (!id || savingPass) return;
    setSavingPass(true);
    try {
      const res = await UnidadTransporteService.generarPassword(id);
      if (res && !res.error) {
        setTemporalPass(res.data ? res.data.password : (res.password || ''));
        Toast.success('Contraseña temporal generada con éxito');
      } else {
        Toast.error(res?.errMsg || 'Error al generar la contraseña temporal');
      }
    } catch (e) {
      Toast.error('Error inesperado al generar contraseña');
    } finally {
      setSavingPass(false);
    }
  };

  // Toggle active status handler
  const handleToggleActive = async () => {
    if (!id || savingActive) return;
    setSavingActive(true);
    try {
      const res = await UnidadTransporteService.toggleActivo(id);
      if (res && !res.error) {
        const nextActive = res.data ? res.data.activo : res.activo;
        setActivo(nextActive);
        setShowActiveModal(false);
        Toast.success(
          nextActive
            ? 'Se habilitó al chofer correctamente'
            : 'Se deshabilitó al chofer correctamente'
        );
      } else {
        Toast.error(res?.errMsg || 'Error al cambiar el estado del chofer');
      }
    } catch (e) {
      Toast.error('Error inesperado al cambiar estado del chofer');
    } finally {
      setSavingActive(false);
    }
  };

  // Form field definitions
  const fields = useMemo(() => [
    {
      name: 'seccion_chofer',
      label: 'Datos del Chofer y Unidad',
      type: 'section',
    },
    {
      name: 'unidad',
      label: 'Unidad',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. TR-01',
      validators: [Validators.required('La unidad es obligatoria')],
    },
    {
      name: 'id_transportista',
      label: 'Transportista',
      type: 'select',
      col: 6,
      required: true,
      options: transportistasOptions,
      validators: [Validators.required('El transportista es obligatorio')],
    },
    {
      name: 'ut_chofer',
      label: 'Chofer Nombre Completo',
      type: 'text',
      col: 12,
      required: true,
      placeholder: 'Ej. Carlos González Morales',
      validators: [Validators.required('El nombre del chofer es obligatorio')],
    },
    {
      name: 'chofer_rfc',
      label: 'RFC',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'ABCD-YYMMDD-XXX',
      validators: [
        Validators.required('El RFC es obligatorio'),
        Validators.rfcFisica('RFC de persona física no válido (formato: ABCD-YYMMDD-XXX)'),
      ],
    },
    {
      name: 'chofer_numero_licencia',
      label: 'Número de Licencia',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. LIC-12345678',
      validators: [Validators.required('El número de licencia es obligatorio')],
    },
    {
      name: 'id_sucursal',
      label: 'Sucursal',
      type: 'select',
      col: 6,
      required: true,
      options: sucursalesOptions,
      validators: [Validators.required('La sucursal es obligatoria')],
    },
    {
      name: 'ut_telefono',
      label: 'Teléfono',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. 3312345678',
      validators: [Validators.required('El teléfono es obligatorio')],
    },
    {
      name: 'ut_cel_modelo',
      label: 'Modelo de Celular',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Samsung Galaxy A32',
      validators: [Validators.required('El modelo de celular es obligatorio')],
    },
    {
      name: 'ut_imei',
      label: 'IMEI',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. 865432041234567',
      validators: [Validators.required('El IMEI es obligatorio')],
    },
    {
      name: 'ut_cel_proveedor',
      label: 'Proveedor de Celular',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Telcel, AT&T',
      validators: [Validators.required('El proveedor de celular es obligatorio')],
    },
    {
      name: 'seccion_domicilio',
      label: 'Domicilio del Chofer',
      type: 'section',
    },
    {
      name: 'chofer_calle',
      label: 'Calle',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Av. Hidalgo',
      validators: [Validators.required('La calle es obligatoria')],
    },
    {
      name: 'chofer_numero',
      label: 'Número Exterior',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. 1234',
      validators: [Validators.required('El número exterior es obligatorio')],
    },
    {
      name: 'codigo_postal',
      label: 'Código Postal',
      type: 'autocomplete',
      col: 6,
      required: true,
      placeholder: 'Buscar código postal (5 dígitos)...',
      minSearchLength: 5,
      debounceMs: 250,
      options: cpOptions,
      onSearch: handleSearchCp,
      onChange: handleCpChange,
      validators: [Validators.required('El código postal es obligatorio')],
    },
    {
      name: 'chofer_id_colonia',
      label: 'Colonia',
      type: 'select',
      col: 6,
      required: true,
      options: coloniasOptions,
      validators: [Validators.required('La colonia es obligatoria')],
    },
    {
      name: 'estado',
      label: 'Estado',
      type: 'text',
      col: 6,
      disabled: true,
      placeholder: 'Auto-completado al indicar el C.P.',
    },
    {
      name: 'ciudad',
      label: 'Ciudad',
      type: 'text',
      col: 6,
      disabled: true,
      placeholder: 'Auto-completada al indicar el C.P.',
    },
  ], [
    transportistasOptions,
    sucursalesOptions,
    coloniasOptions,
    cpOptions,
    handleSearchCp,
    handleCpChange,
  ]);

  // Header action buttons for Edit mode
  const headerActions = useMemo(() => {
    if (!id) return null;

    const canGeneratePassword = hasAccess(ACCESS.CATALOGO_UNIDADES_DE_TRANSPORTE.GENERAR_CONTRASENA);
    const canEdit = hasAccess(ACCESS.CATALOGO_UNIDADES_DE_TRANSPORTE.EDITAR_CATALOGO);

    return (
      <div className="flex items-center gap-2 flex-wrap">
        {canGeneratePassword && (
          <button
            type="button"
            onClick={() => {
              setTemporalPass(null);
              setShowPassModal(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg shadow-2xs transition-colors cursor-pointer min-h-[38px]"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span>Generar Contraseña</span>
          </button>
        )}

        {canEdit && (
          <button
            type="button"
            onClick={() => setShowActiveModal(true)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg shadow-2xs transition-colors cursor-pointer min-h-[38px] ${
              activo
                ? 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200'
                : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${activo ? 'bg-red-500' : 'bg-emerald-500'}`} />
            <span>{activo ? 'Deshabilitar Chofer' : 'Habilitar Chofer'}</span>
          </button>
        )}
      </div>
    );
  }, [id, activo, hasAccess]);

  return (
    <>
      <CatalogForm
        title="Unidad de Transporte"
        subtitle="Administración de unidades vehiculares, operadores y datos de acceso a la app móvil"
        fields={fields}
        initialValues={{
          unidad: '',
          id_transportista: '',
          ut_chofer: '',
          chofer_rfc: '',
          chofer_numero_licencia: '',
          id_sucursal: '',
          ut_telefono: '',
          ut_cel_modelo: '',
          ut_imei: '',
          ut_cel_proveedor: '',
          chofer_calle: '',
          chofer_numero: '',
          codigo_postal: '',
          chofer_id_colonia: '',
          estado: '',
          ciudad: '',
        }}
        id={id}
        loadData={loadData}
        onSubmit={handleSubmit}
        backUrl="/Catalogo/UnidadesTransporte"
        headerActions={headerActions}
      />

      {/* Modal: Generar Contraseña Temporal */}
      {showPassModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-gray-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Generar Nueva Contraseña</h3>
            
            {temporalPass ? (
              <div className="my-5 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <p className="text-xs font-medium text-blue-700 uppercase tracking-wider mb-1">Contraseña Temporal Generada</p>
                <p className="text-2xl font-mono font-bold text-blue-900 tracking-wider select-all">{temporalPass}</p>
                <p className="text-xs text-blue-600 mt-2">Esta contraseña es de un solo uso para la app Exxprezo Delivery.</p>
              </div>
            ) : (
              <div className="my-4 text-sm text-gray-600 space-y-2">
                <p>Se generará una contraseña para la aplicación móvil <strong>Exxprezo Delivery</strong>.</p>
                <p>En caso de existir una contraseña previa, será revocada y reemplazada.</p>
                <p className="text-xs text-gray-500">La nueva contraseña será de un solo uso y la aplicación solicitará al usuario crear una clave personal.</p>
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowPassModal(false);
                  setTemporalPass(null);
                }}
                disabled={savingPass}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer"
              >
                {temporalPass ? 'Cerrar' : 'Cancelar'}
              </button>
              {!temporalPass && (
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  disabled={savingPass}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-xs cursor-pointer disabled:opacity-50"
                >
                  {savingPass && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  <span>Confirmar y Generar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Habilitar / Deshabilitar Chofer */}
      {showActiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-gray-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {activo ? 'Deshabilitar Chofer' : 'Habilitar Chofer'}
            </h3>
            
            <div className="my-4 text-sm text-gray-600">
              <p>
                ¿Está seguro de que desea <strong>{activo ? 'deshabilitar' : 'habilitar'}</strong> a este chofer?
              </p>
              {activo && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                  <strong>Aviso Importante:</strong> Una vez deshabilitado, el chofer perderá inmediatamente el acceso para operar en la aplicación Exxprezo Delivery.
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowActiveModal(false)}
                disabled={savingActive}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleToggleActive}
                disabled={savingActive}
                className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-xs cursor-pointer disabled:opacity-50 ${
                  activo ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {savingActive && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                <span>{activo ? 'Sí, Deshabilitar' : 'Sí, Habilitar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnidadTransporteForm;
